import React, { useState } from 'react';
import styled from 'styled-components';
import SvgIcon from '@twicapp/ui/components/SvgIcon';
import Body2 from '@twicapp/ui/components/Body2';
import Icon from '@twicapp/ui/components/legacy/Icon';
import Menu from '@twicapp/ui/components/Menu';
import MenuLabel from '@twicapp/ui/components/MenuLabel';
import MenuItem from '@twicapp/ui/components/MenuItem';
import { ReactComponent as UserSvg } from '@twicapp/ui/icons/legacy/user.svg';
import { ReactComponent as BankSvg } from '@twicapp/ui/icons/bank.svg';
import { ReactComponent as DashboardSvg } from '@twicapp/ui/icons/legacy/dashboard.svg';
import { ReactComponent as CreditCardSvg } from '@twicapp/ui/icons/legacy/credit-card.svg';
import { ReactComponent as NotificationSvg } from '@twicapp/ui/icons/custom-attributes.svg';
import { ReactComponent as LockSvg } from '@twicapp/ui/icons/lock.svg';
import { ReactComponent as CaretDownSvg } from '@twicapp/ui/icons/caret-down.svg';
import { ReactComponent as CaretUpSvg } from '@twicapp/ui/icons/caret-up.svg';
import { SCREEN_WIDTHS } from '@twicapp/ui/constants/breakpoints';

import { ReactComponent as ExternalLinkIcon } from 'assets/images/icons/external-link.svg';
import SideNavigationBar from './SideNavigationBar';
import SideLink from './SideLink';
import Backlink from 'components/common/Backlink';

import { SETTINGS_PAGE_ELEMENTS } from 'constants/testSelectors';
import { isTwicCardEnabled } from 'utils/employee';
import { SettingsRoutes } from 'utils/routeGenerator';
import {
  SVG_ICON_SIZES,
  SVG_ICON_COLORS,
} from '@twicapp/ui/constants/properties';

const Container = styled.div.attrs({ role: 'navigation' })``;

const MobileContainer = styled.div`
  position: relative;
  margin-bottom: 20px;
  display: block;

  @media (min-width: ${SCREEN_WIDTHS.MOBILE_LANDSCAPE}) {
    display: none;
  }
`;

const DesktopContainer = styled.div`
  width: 202px;
  display: none;

  @media (min-width: ${SCREEN_WIDTHS.MOBILE_LANDSCAPE}) {
    display: flex;
    flex-direction: column;
  }
`;

const AccountContainer = styled.div``;

const CardContainer = styled.div`
  margin-top: 24px;
`;

const DependentContainer = styled.div`
  margin-top: 24px;
`;

const StyledNotificationIcon = styled(SvgIcon).attrs({
  icon: NotificationSvg,
  size: SVG_ICON_SIZES.MEDIUM,
  stroke: SVG_ICON_COLORS.PRIMARY,
})``;

const StyledUserIcon = styled(SvgIcon).attrs({
  icon: UserSvg,
  size: SVG_ICON_SIZES.MEDIUM,
  stroke: SVG_ICON_COLORS.PRIMARY,
})``;

const StyledBankIcon = styled(SvgIcon).attrs({
  icon: BankSvg,
  size: SVG_ICON_SIZES.MEDIUM,
  stroke: SVG_ICON_COLORS.PRIMARY,
})``;

const StyledDashboardIcon = styled(SvgIcon).attrs({
  icon: DashboardSvg,
  size: SVG_ICON_SIZES.MEDIUM,
  stroke: SVG_ICON_COLORS.PRIMARY,
})``;

const ExternalLinkIconStyled = styled(Icon)`
  &:hover {
    cursor: pointer;
  }
  padding-left: 16px;
  svg {
    fill: none !important;
  }
  svg > path {
    stroke-width: 1.16px !important;
  }
`;

const StyledCreditCardIcon = styled(SvgIcon).attrs({
  icon: CreditCardSvg,
  size: SVG_ICON_SIZES.MEDIUM,
  stroke: SVG_ICON_COLORS.PRIMARY,
})``;

const StyledLockIcon = styled(SvgIcon).attrs({
  icon: LockSvg,
  size: SVG_ICON_SIZES.MEDIUM,
  stroke: SVG_ICON_COLORS.PRIMARY,
})``;

const ExternalLinkStyled = styled.a`
  display: flex;
  gap: 10px;
`;

const DeleteLinkStyled = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  width: 180px;
  height: 40px;
  border-radius: 7px;
  &:hover {
    background-color: #eeeff2;
  }
`;

const Text = styled(Body2)`
  display: block;
`;

const NavigationDropdown = styled.button`
  cursor: pointer;
  width: 100%;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  background: none;
  border: ${({
    theme: {
      colors: {
        border: { primary },
      },
    },
  }) => `1px solid ${primary}`};
`;

const CaretDownIcon = styled(SvgIcon).attrs({
  icon: CaretDownSvg,
  size: SVG_ICON_SIZES.MEDIUM,
  fill: SVG_ICON_COLORS.INTERACTIVE,
})``;

const CaretUpIcon = styled(SvgIcon).attrs({
  icon: CaretUpSvg,
  size: SVG_ICON_SIZES.MEDIUM,
  fill: SVG_ICON_COLORS.INTERACTIVE,
})``;

const StyledMenu = styled(Menu)`
  width: 100%;
`;

const ActiveTabText = styled(Body2)`
  color: ${({
    theme: {
      colors: {
        text: { interactive },
      },
    },
  }) => interactive};
`;

interface Dependent {
  dependent_id: string;
  first_name: string;
  last_name: string;
}

const renderActiveTabText = (active_path: string) => {
  switch (active_path) {
    case SettingsRoutes.profile():
      return 'Profile';
    case SettingsRoutes.payment():
      return 'Payments';
    case SettingsRoutes.notifications():
      return 'Notifications';
    case SettingsRoutes.integrations():
      return 'Integrations';
    case SettingsRoutes.security():
      return 'Security';
    case SettingsRoutes.pretaxCardList():
    case SettingsRoutes.pretaxCardNew():
      return 'Benefits Card';
    case SettingsRoutes.twicCardList():
    case SettingsRoutes.twicCardNew():
    case SettingsRoutes.twicCardEdit():
      return 'Twic Card';
    case SettingsRoutes.bankAccountList():
      return 'Bank Account';
    default:
      return 'Settings';
  }
};

const SettingsFilter = ({
  active_path,
  company,
  employee,
  pretax_accounts = [],
  dependents = [],
}: $TSFixMe) => {
  const [is_open, setIsOpen] = useState(false);
  const {
    settings: { slack, sso },
  } = company;
  const {
    settings: { is_cdh_enabled, country },
  } = employee;

  const is_american = country === 'us';
  const is_twic_card_enabled = isTwicCardEnabled({ company, employee });

  const show_integration = !!slack;
  const show_security = !sso;
  const show_dependents =
    (is_cdh_enabled && is_american) || dependents.length > 0;

  const show_twic_card = is_twic_card_enabled;
  const show_pretax_card = pretax_accounts.length > 0;
  const show_cards = is_american && (show_twic_card || show_pretax_card);

  return (
    <Container data-testid={SETTINGS_PAGE_ELEMENTS.SIDE_NAV_LINK}>
      <MobileContainer>
        <Backlink />
        <NavigationDropdown onClick={() => setIsOpen(true)}>
          <ActiveTabText>{renderActiveTabText(active_path)}</ActiveTabText>
          {is_open ? <CaretUpIcon /> : <CaretDownIcon />}
        </NavigationDropdown>
        <StyledMenu open={is_open} onClose={() => setIsOpen(false)}>
          <MenuLabel>Twic</MenuLabel>
          <MenuItem
            start_text="Profile"
            start_icon={UserSvg}
            to={SettingsRoutes.profile()}
          />
          <MenuItem
            start_text="Notifications"
            start_icon={NotificationSvg}
            to={SettingsRoutes.notifications()}
          />
          <MenuItem
            start_text="Payments"
            start_icon={BankSvg}
            to={SettingsRoutes.payment()}
          />
          <MenuItem
            start_text="Integrations"
            start_icon={DashboardSvg}
            to={SettingsRoutes.integrations()}
          />
          {show_cards && <MenuLabel>Twic</MenuLabel>}
          {show_twic_card && (
            <MenuItem
              start_text="Twic Card"
              start_icon={CreditCardSvg}
              to={SettingsRoutes.twicCardList()}
            />
          )}
          {show_pretax_card && (
            <MenuItem
              start_text="Benefits Card"
              start_icon={CreditCardSvg}
              to={SettingsRoutes.pretaxCardList()}
            />
          )}
        </StyledMenu>
      </MobileContainer>
      <DesktopContainer>
        <AccountContainer>
          <SideNavigationBar header="Account Settings">
            <SideLink
              key="Profile"
              route={SettingsRoutes.profile()}
              start_icon={<StyledUserIcon />}
            >
              Profile
            </SideLink>
            <SideLink
              key="Notifications"
              route={SettingsRoutes.notifications()}
              start_icon={<StyledNotificationIcon />}
            >
              Notifications
            </SideLink>
            <SideLink
              key="Payments"
              route={SettingsRoutes.payment()}
              start_icon={<StyledBankIcon />}
            >
              Payments
            </SideLink>
          </SideNavigationBar>
          {show_security && (
            <SideNavigationBar>
              <SideLink
                key="Security"
                start_icon={<StyledLockIcon />}
                route={SettingsRoutes.security()}
              >
                Security
              </SideLink>
            </SideNavigationBar>
          )}
          {show_integration && (
            <SideNavigationBar>
              <SideLink
                key="Integrations"
                start_icon={<StyledDashboardIcon />}
                route={SettingsRoutes.integrations()}
              >
                Integrations
              </SideLink>
            </SideNavigationBar>
          )}
          <DeleteLinkStyled>
            <ExternalLinkStyled
              key="DeleteAccount"
              href="https://www.requesteasy.com/5dfe-0936"
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLinkIconStyled
                component={ExternalLinkIcon}
                style={{ fontSize: '22px' }}
              />
              <Text>{'My Personal Data'}</Text>
            </ExternalLinkStyled>
          </DeleteLinkStyled>
        </AccountContainer>

        {show_cards && (
          <CardContainer>
            <SideNavigationBar header="Card Settings">
              {show_twic_card && (
                <SideLink
                  key="Twic Card"
                  start_icon={<StyledCreditCardIcon />}
                  route={SettingsRoutes.twicCardList()}
                >
                  Twic Card
                </SideLink>
              )}
              {show_pretax_card && (
                <SideLink
                  key="Benefits Card"
                  start_icon={<StyledCreditCardIcon />}
                  route={SettingsRoutes.pretaxCardList()}
                >
                  Benefits Card
                </SideLink>
              )}
            </SideNavigationBar>
          </CardContainer>
        )}
        {show_dependents && (
          <DependentContainer>
            <SideNavigationBar header="Dependents">
              {dependents.map(
                ({ dependent_id, first_name, last_name }: Dependent) => (
                  <SideLink
                    key={dependent_id}
                    route={SettingsRoutes.pretaxDependentsDetail({
                      dependent_id,
                    })}
                    start_icon={<StyledUserIcon />}
                  >
                    {`${first_name} ${last_name}`}
                  </SideLink>
                )
              )}
            </SideNavigationBar>
          </DependentContainer>
        )}
      </DesktopContainer>
    </Container>
  );
};

export default SettingsFilter;
